'use client'
import React, { useState, useEffect, useRef, KeyboardEvent } from 'react'
import { useWebContainerStore } from '@/lib/webcontainer-store'
import { Terminal as TerminalIcon, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Terminal as XTerm } from '@xterm/xterm'
import { FitAddon } from 'xterm-addon-fit'

interface TerminalLine {
  type: 'output' | 'command' | 'error'
  text: string
  timestamp: number
}

interface TerminalTab {
  id: string
  name: string
  lines: TerminalLine[]
  currentCommand: string
  history: string[]
  historyIndex: number
}

export function InteractiveTerminal() {
  const { instance, terminalOutput, isBooting, isReady } = useWebContainerStore()
  const xtermMapRef = useRef<Record<string, { xterm: XTerm; fitAddon: FitAddon }>>({})
  const terminalContainerRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const lastOutputIndexRef = useRef(0)
  const [tabs, setTabs] = useState<TerminalTab[]>([
    {
      id: '1',
      name: 'Terminal 1',
      lines: [],
      currentCommand: '',
      history: [],
      historyIndex: -1
    }
  ])
  const [activeTab, setActiveTab] = useState('1')
  const inputRef = useRef<HTMLInputElement>(null)
  const welcomedTabsRef = useRef<Set<string>>(new Set())

  const ensureTerminal = (tabId: string) => {
    if (!xtermMapRef.current[tabId]) {
      const xterm = new XTerm({
        convertEol: true,
        cursorBlink: true,
        fontSize: 12
      })
      const fitAddon = new FitAddon()
      xterm.loadAddon(fitAddon)
      xtermMapRef.current[tabId] = { xterm, fitAddon }
    }
    return xtermMapRef.current[tabId]
  }

  const writeToTerminal = (tabId: string, data: string, newline = false) => {
    const terminal = xtermMapRef.current[tabId]
    if (!terminal) return
    if (newline) {
      terminal.xterm.writeln(data)
    } else {
      terminal.xterm.write(data)
    }
  }

  const toText = (data: string | Uint8Array) => {
    if (typeof data === 'string') return data
    return new TextDecoder().decode(data)
  }

  // Convert WebContainer terminal output to terminal lines + ANSI rendering
  useEffect(() => {
    if (terminalOutput.length > lastOutputIndexRef.current) {
      const terminal = ensureTerminal(activeTab)
      const newLines = terminalOutput.slice(lastOutputIndexRef.current)
      newLines.forEach((line) => {
        writeToTerminal(activeTab, line, true)
      })
      lastOutputIndexRef.current = terminalOutput.length

      setTabs(prevTabs => prevTabs.map(tab => 
        tab.id === activeTab
          ? {
              ...tab,
              lines: [
                ...tab.lines,
                ...newLines.map(line => ({
                  type: line.startsWith('[error]') || line.toLowerCase().includes('error') ? 'error' : 'output',
                  text: line,
                  timestamp: Date.now()
                }))
              ]
            }
          : tab
      ))
    }
  }, [terminalOutput, activeTab])

  useEffect(() => {
    const container = terminalContainerRefs.current[activeTab]
    if (!container) return

    const terminal = ensureTerminal(activeTab)
    if (!terminal.xterm.element || !container.contains(terminal.xterm.element)) {
      terminal.xterm.open(container)
    }
    terminal.fitAddon.fit()

    if (!welcomedTabsRef.current.has(activeTab)) {
      const message = isBooting
        ? 'Initializing WebContainer...'
        : 'Type commands below to interact with WebContainer...'
      terminal.xterm.writeln(message)
      welcomedTabsRef.current.add(activeTab)
    }
  }, [activeTab])

  useEffect(() => {
    if (isReady && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isReady, activeTab])

  useEffect(() => {
    return () => {
      Object.values(xtermMapRef.current).forEach(({ xterm }) => xterm.dispose())
      xtermMapRef.current = {}
    }
  }, [])

  const executeCommand = async (command: string, tabId: string) => {
    if (!command.trim() || !instance) return
    ensureTerminal(tabId)

    // Add command to terminal
    const commandLine: TerminalLine = {
      type: 'command',
      text: `$ ${command}`,
      timestamp: Date.now()
    }

    setTabs(prevTabs => prevTabs.map(tab => {
      if (tab.id === tabId) {
        const newHistory = [...tab.history, command]
        return {
          ...tab,
          lines: [...tab.lines, commandLine],
          history: newHistory,
          historyIndex: newHistory.length,
          currentCommand: ''
        }
      }
      return tab
    }))

    writeToTerminal(tabId, `$ ${command}`, true)

    try {
      // Execute command in WebContainer
      const process = await instance.spawn(command.split(' ')[0], command.split(' ').slice(1))
      
      // Capture output
      const output: string[] = []
      
      process.output.pipeTo(new WritableStream({
        write(data) {
          const text = toText(data)
          output.push(text)
          writeToTerminal(tabId, text)
          const outputLine: TerminalLine = {
            type: 'output',
            text: text,
            timestamp: Date.now()
          }
          setTabs(prevTabs => prevTabs.map(tab => 
            tab.id === tabId ? { ...tab, lines: [...tab.lines, outputLine] } : tab
          ))
        }
      }))

      const exitCode = await process.exit

      if (exitCode !== 0) {
        const errorLine: TerminalLine = {
          type: 'error',
          text: `Command exited with code ${exitCode}`,
          timestamp: Date.now()
        }
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.id === tabId ? { ...tab, lines: [...tab.lines, errorLine] } : tab
        ))
        writeToTerminal(tabId, `Command exited with code ${exitCode}`, true)
      }
    } catch (error) {
      const errorLine: TerminalLine = {
        type: 'error',
        text: `Error: ${error instanceof Error ? error.message : String(error)}`,
        timestamp: Date.now()
      }
      setTabs(prevTabs => prevTabs.map(tab => 
        tab.id === tabId ? { ...tab, lines: [...tab.lines, errorLine] } : tab
      ))
      writeToTerminal(tabId, errorLine.text, true)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, tabId: string) => {
    const currentTab = tabs.find(t => t.id === tabId)
    if (!currentTab) return

    if (e.key === 'Enter') {
      e.preventDefault()
      executeCommand(currentTab.currentCommand, tabId)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (currentTab.historyIndex > 0) {
        const newIndex = currentTab.historyIndex - 1
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.id === tabId 
            ? { ...tab, historyIndex: newIndex, currentCommand: tab.history[newIndex] }
            : tab
        ))
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (currentTab.historyIndex < currentTab.history.length - 1) {
        const newIndex = currentTab.historyIndex + 1
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.id === tabId 
            ? { ...tab, historyIndex: newIndex, currentCommand: tab.history[newIndex] }
            : tab
        ))
      } else {
        setTabs(prevTabs => prevTabs.map(tab => 
          tab.id === tabId 
            ? { ...tab, historyIndex: tab.history.length, currentCommand: '' }
            : tab
        ))
      }
    }
  }

  const addNewTab = () => {
    const newId = String(tabs.length + 1)
    ensureTerminal(newId)
    setTabs([...tabs, {
      id: newId,
      name: `Terminal ${newId}`,
      lines: [],
      currentCommand: '',
      history: [],
      historyIndex: -1
    }])
    setActiveTab(newId)
  }

  const closeTab = (tabId: string) => {
    if (tabs.length === 1) return // Keep at least one tab
    
    const newTabs = tabs.filter(t => t.id !== tabId)
    setTabs(newTabs)

    const terminal = xtermMapRef.current[tabId]
    if (terminal) {
      terminal.xterm.dispose()
      delete xtermMapRef.current[tabId]
    }
    
    if (activeTab === tabId) {
      setActiveTab(newTabs[0].id)
    }
  }

  const currentTab = tabs.find(t => t.id === activeTab)

  return (
    <div className="h-full flex flex-col bg-[color:var(--terminal-bg)]">
      <div className="relative z-10 flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <TerminalIcon className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-foreground">Interactive Terminal</span>
          {isBooting && (
            <span className="text-xs text-muted-foreground animate-pulse">Booting WebContainer...</span>
          )}
          {isReady && (
            <span className="text-xs text-accent">Ready</span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={addNewTab}
          className="h-7 px-2 text-xs"
        >
          + New Tab
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="relative z-10 w-full justify-start rounded-none border-b border-border bg-muted/30 px-2 text-foreground">
          {tabs.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="relative data-[state=active]:bg-muted"
            >
              {tab.name}
              {tabs.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    closeTab(tab.id)
                  }}
                  className="ml-2 hover:text-destructive"
                >
                  ×
                </button>
              )}
            </TabsTrigger>
          ))}
        </TabsList>


        {tabs.map(tab => (
          <TabsContent key={tab.id} value={tab.id} className="relative z-0 flex-1 flex flex-col mt-0" forceMount>
            <div
              ref={(el) => {
                terminalContainerRefs.current[tab.id] = el
              }}
              className="relative flex-1 min-h-[120px] bg-[color:var(--terminal-bg)] text-foreground"
              onClick={() => inputRef.current?.focus()}
            />

            <div className="border-t border-border p-2 bg-muted/20">
              <div className="flex items-center gap-2">
                <span className="text-accent font-mono text-sm">$</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={tab.currentCommand}
                  onChange={(e) => setTabs(prevTabs => prevTabs.map(t => 
                    t.id === tab.id ? { ...t, currentCommand: e.target.value } : t
                  ))}
                  onKeyDown={(e) => handleKeyDown(e, tab.id)}
                  disabled={!isReady}
                  placeholder={isReady ? "Type command and press Enter..." : "Waiting for WebContainer..."}
                  className="flex-1 bg-transparent border-none outline-none text-sm font-mono text-foreground placeholder:text-muted-foreground disabled:opacity-50"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => executeCommand(tab.currentCommand, tab.id)}
                  disabled={!isReady || !tab.currentCommand.trim()}
                  className="h-7 px-2"
                >
                  <Play className="h-3 w-3" />
                </Button>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Tip: Use ↑↓ for command history • Try: ls, cat [file], pwd, echo "hello"
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
